import React, { useEffect, useState } from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { companyAPI } from '../../utils/api';
import { useToast } from '../../hooks/use-toast';

export const HOME_DEFAULTS = {
  hero_subtitle: 'Serving MNCs across India',
  stat1_value: '13+', stat1_label: 'Years Experience',
  stat2_value: '50+', stat2_label: 'Corporate Clients',
  stat3_value: '100+', stat3_label: 'Cities Covered',
  stat4_value: '24/7', stat4_label: 'Support',
  services_heading: 'Our Services',
  services_subheading: 'Comprehensive transportation solutions for all your needs',
  why_heading: 'Why Choose Ecoway Cabs?',
  why_subheading: 'The trusted choice for corporate transportation',
  why_card1_title: 'Reliable & Safe', why_card1_desc: 'Verified drivers, well-maintained vehicles, and GPS tracking',
  why_card2_title: 'Cost-Effective', why_card2_desc: 'Transparent pricing with no hidden charges',
  why_card3_title: 'Professional Service', why_card3_desc: 'Courteous drivers, punctual service, dedicated support',
  testimonials_heading: 'What Our Clients Say', testimonials_subheading: 'Trusted by leading organizations',
  cta_heading: 'Ready to Get Started?', cta_subheading: 'Contact us today for a customized transportation solution',
};

const Field = ({ label, k, hc, setHc, testid }) => (
  <div>
    <Label>{label}</Label>
    <Input
      value={hc[k] ?? ''}
      placeholder={HOME_DEFAULTS[k]}
      onChange={(e) => setHc({ ...hc, [k]: e.target.value })}
      data-testid={testid}
    />
  </div>
);

export const HomeContentManager = () => {
  const { toast } = useToast();
  const [hc, setHc] = useState(HOME_DEFAULTS);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    companyAPI.getInfo().then((res) => {
      setHc({ ...HOME_DEFAULTS, ...(res.data.home_content || {}) });
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await companyAPI.updateInfo({ home_content: hc });
      toast({ title: 'Success', description: 'Home page content updated' });
    } catch (err) {
      toast({ title: 'Error', description: 'Failed to save', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardContent className="p-6 space-y-4">
          <h3 className="text-lg font-semibold">Hero Subtitle</h3>
          <Field label="Hero subtitle (shown after 'Established since {year} |')" k="hero_subtitle" hc={hc} setHc={setHc} testid="home-hero-subtitle-input" />
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-6 space-y-4">
          <h3 className="text-lg font-semibold">Stats</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Stat 1 Value" k="stat1_value" hc={hc} setHc={setHc} testid="home-stat1-value-input" />
            <Field label="Stat 1 Label" k="stat1_label" hc={hc} setHc={setHc} testid="home-stat1-label-input" />
            <Field label="Stat 2 Value" k="stat2_value" hc={hc} setHc={setHc} testid="home-stat2-value-input" />
            <Field label="Stat 2 Label" k="stat2_label" hc={hc} setHc={setHc} testid="home-stat2-label-input" />
            <Field label="Stat 3 Value" k="stat3_value" hc={hc} setHc={setHc} testid="home-stat3-value-input" />
            <Field label="Stat 3 Label" k="stat3_label" hc={hc} setHc={setHc} testid="home-stat3-label-input" />
            <Field label="Stat 4 Value" k="stat4_value" hc={hc} setHc={setHc} testid="home-stat4-value-input" />
            <Field label="Stat 4 Label" k="stat4_label" hc={hc} setHc={setHc} testid="home-stat4-label-input" />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-6 space-y-4">
          <h3 className="text-lg font-semibold">Section Headings</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Services Heading" k="services_heading" hc={hc} setHc={setHc} testid="home-services-heading-input" />
            <Field label="Services Subheading" k="services_subheading" hc={hc} setHc={setHc} testid="home-services-subheading-input" />
            <Field label="Why Choose Us Heading" k="why_heading" hc={hc} setHc={setHc} testid="home-why-heading-input" />
            <Field label="Why Choose Us Subheading" k="why_subheading" hc={hc} setHc={setHc} testid="home-why-subheading-input" />
            <Field label="Testimonials Heading" k="testimonials_heading" hc={hc} setHc={setHc} testid="home-testimonials-heading-input" />
            <Field label="Testimonials Subheading" k="testimonials_subheading" hc={hc} setHc={setHc} testid="home-testimonials-subheading-input" />
            <Field label="CTA Heading" k="cta_heading" hc={hc} setHc={setHc} testid="home-cta-heading-input" />
            <Field label="CTA Subheading" k="cta_subheading" hc={hc} setHc={setHc} testid="home-cta-subheading-input" />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-6 space-y-4">
          <h3 className="text-lg font-semibold">"Why Choose Us" Cards</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Card 1 Title" k="why_card1_title" hc={hc} setHc={setHc} testid="home-why-card1-title-input" />
            <Field label="Card 1 Description" k="why_card1_desc" hc={hc} setHc={setHc} testid="home-why-card1-desc-input" />
            <Field label="Card 2 Title" k="why_card2_title" hc={hc} setHc={setHc} testid="home-why-card2-title-input" />
            <Field label="Card 2 Description" k="why_card2_desc" hc={hc} setHc={setHc} testid="home-why-card2-desc-input" />
            <Field label="Card 3 Title" k="why_card3_title" hc={hc} setHc={setHc} testid="home-why-card3-title-input" />
            <Field label="Card 3 Description" k="why_card3_desc" hc={hc} setHc={setHc} testid="home-why-card3-desc-input" />
          </div>
        </CardContent>
      </Card>

      <Button onClick={handleSave} disabled={saving} data-testid="home-content-save-button">
        {saving ? 'Saving...' : 'Save Home Page Content'}
      </Button>
    </div>
  );
};

export default HomeContentManager;
